/**
 * Turns the marketing consents of the contact form into a real newsletter signup.
 *
 * Shopify's contact form only emails the shop: it never creates a customer and never touches
 * the customer's marketing state, and Flow has no "contact form submitted" trigger. The
 * newsletter (`form 'customer'`) is the only native way a storefront can subscribe an address;
 * it runs the shop's double opt-in (confirmation email, Pending until the link is clicked)
 * exactly like the footer.
 *
 * Why it runs after the contact submit, not alongside it:
 * - the customer form is protected by hCaptcha, so fetch() gets 400 "Missing CAPTCHA token";
 *   the documented route is `Shopify.captcha.protect(form, callback)` plus a native submit;
 * - two captcha-protected submits on one page interfere (the contact post came back
 *   "invalid parameters", tested 2026-10-13), while a single one works.
 * So the contact form is left untouched. On its submit, when the email box is ticked, the
 * address and the SMS flag go to sessionStorage; the success page that Shopify renders after
 * the post (`contact-posted` attribute) reads them once, deletes them, and submits the hidden
 * customer form into a hidden iframe.
 *
 * Tags carry the consents to Flow: `newsletter` always, `consent_sms_phone` when the SMS and
 * phone box is ticked too. Nothing happens without the email box, because the customer form
 * always subscribes the email address; SMS consent on its own stays a field in the contact
 * notification email.
 *
 * Storage key `altdecor_contact_optin` (sessionStorage) is listed in the cookie declaration.
 * Documented in `materials/altdecor-docs/contact-form-subject-and-consent.md`.
 */
class ContactNewsletterOptin extends HTMLElement {
  static STORAGE_KEY = 'altdecor_contact_optin';

  #contactForm = null;
  #onSubmit = () => this.#remember();

  connectedCallback() {
    if (this.hasAttribute('contact-posted')) {
      this.#subscribe();
      return;
    }
    this.#contactForm = document.getElementById(this.getAttribute('contact-form-id'));
    this.#contactForm?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#contactForm?.removeEventListener('submit', this.#onSubmit);
  }

  #remember() {
    const emailConsent = this.#contactForm.querySelector('input[name="contact[Email consent]"]');
    const smsConsent = this.#contactForm.querySelector('input[name="contact[SMS and phone consent]"]');
    const email = this.#contactForm.querySelector('input[name="contact[email]"]')?.value.trim();

    try {
      if (!emailConsent?.checked || !email) {
        sessionStorage.removeItem(ContactNewsletterOptin.STORAGE_KEY);
        return;
      }
      sessionStorage.setItem(
        ContactNewsletterOptin.STORAGE_KEY,
        JSON.stringify({ email, sms: Boolean(smsConsent?.checked) })
      );
    } catch {
      // Storage blocked: the message still goes out, only the signup is skipped.
    }
  }

  #subscribe() {
    let optin = null;
    try {
      optin = JSON.parse(sessionStorage.getItem(ContactNewsletterOptin.STORAGE_KEY));
      sessionStorage.removeItem(ContactNewsletterOptin.STORAGE_KEY);
    } catch {
      return;
    }
    if (!optin?.email) return;

    const newsletterForm = this.querySelector('form');
    const frame = this.querySelector('iframe');
    if (!newsletterForm || !frame || !window.Shopify?.captcha?.protect) {
      console.warn('contact-newsletter-optin: captcha API missing, signup skipped');
      return;
    }

    newsletterForm.target = frame.name;
    newsletterForm.querySelector('input[name="contact[email]"]').value = optin.email;
    newsletterForm.querySelector('input[name="contact[tags]"]').value = optin.sms
      ? 'newsletter,consent_sms_phone'
      : 'newsletter';

    window.Shopify.captcha.protect(newsletterForm, () => newsletterForm.submit());
  }
}

if (!customElements.get('contact-newsletter-optin')) {
  customElements.define('contact-newsletter-optin', ContactNewsletterOptin);
}
